import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BondPayload, ConvertApplicationPayload, CreateCollaboratorPayload, ListCollaboratorsParams,
  UpdateCollaboratorPayload, ativarVinculo, atualizarColaborador, atualizarVinculo, buscarColaborador,
  buscarDuplicidades, cancelarVinculo, conciliarVinculo, confirmarVinculo, converterCandidatura,
  criarColaborador, criarVinculo, exportarAdmissao, historicoVinculo, listarColaboradores,
  listarEmpresasAtivas, pendenciasVinculo, registrarMatriculaFolha, validarColaborador, vinculoDaCandidatura,
} from '../api';

export const PESSOAS_KEYS = {
  all: ['pessoas'] as const,
  lista: (p: ListCollaboratorsParams) => ['pessoas', 'colaboradores', 'lista', p] as const,
  detalhe: (id: string) => ['pessoas', 'colaboradores', id] as const,
  duplicidades: (p: object) => ['pessoas', 'duplicidades', p] as const,
  pendencias: (bondId: string) => ['pessoas', 'vinculos', bondId, 'pendencias'] as const,
  historico: (bondId: string) => ['pessoas', 'vinculos', bondId, 'historico'] as const,
  candidatura: (applicationId: string) => ['pessoas', 'candidatura', applicationId] as const,
  empresas: ['pessoas', 'empresas'] as const,
};

export function useColaboradores(params: ListCollaboratorsParams) {
  return useQuery({
    queryKey: PESSOAS_KEYS.lista(params),
    queryFn: () => listarColaboradores(params),
    placeholderData: keepPreviousData,
  });
}

export function useColaborador(id: string | undefined) {
  return useQuery({
    queryKey: PESSOAS_KEYS.detalhe(id ?? ''),
    queryFn: () => buscarColaborador(id as string),
    enabled: !!id,
  });
}

export function useDuplicidades(params: { cpf?: string; name?: string; birthDate?: string; email?: string }, enabled: boolean) {
  return useQuery({
    queryKey: PESSOAS_KEYS.duplicidades(params),
    queryFn: () => buscarDuplicidades(params),
    enabled,
    staleTime: 30_000,
  });
}

export function usePendencias(bondId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: PESSOAS_KEYS.pendencias(bondId ?? ''),
    queryFn: () => pendenciasVinculo(bondId as string),
    enabled: !!bondId && enabled,
  });
}

export function useHistoricoVinculo(bondId: string | undefined) {
  return useQuery({
    queryKey: PESSOAS_KEYS.historico(bondId ?? ''),
    queryFn: () => historicoVinculo(bondId as string),
    enabled: !!bondId,
  });
}

export function useEmpresasAtivas() {
  return useQuery({ queryKey: PESSOAS_KEYS.empresas, queryFn: listarEmpresasAtivas, staleTime: 5 * 60_000 });
}

export function useVinculoDaCandidatura(applicationId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: PESSOAS_KEYS.candidatura(applicationId ?? ''),
    queryFn: () => vinculoDaCandidatura(applicationId as string),
    enabled: !!applicationId && enabled,
  });
}

/** Toda escrita invalida o módulo inteiro: lista, detalhe, pendências e histórico mudam juntos. */
function useInvalidatingMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: PESSOAS_KEYS.all }),
  });
}

export const useCriarColaborador = () =>
  useInvalidatingMutation((payload: CreateCollaboratorPayload) => criarColaborador(payload));

export const useAtualizarColaborador = (id: string) =>
  useInvalidatingMutation((payload: UpdateCollaboratorPayload) => atualizarColaborador(id, payload));

export const useValidarColaborador = (id: string) => useInvalidatingMutation((_: void) => validarColaborador(id));

export const useCriarVinculo = (personId: string) =>
  useInvalidatingMutation((payload: BondPayload) => criarVinculo(personId, payload));

export const useAtualizarVinculo = () =>
  useInvalidatingMutation(({ id, payload }: { id: string; payload: Partial<BondPayload> }) => atualizarVinculo(id, payload));

export const useExportarAdmissao = () => useInvalidatingMutation((id: string) => exportarAdmissao(id));

export const useRegistrarMatriculaFolha = () =>
  useInvalidatingMutation(({ id, value }: { id: string; value: string }) => registrarMatriculaFolha(id, value));

export const useConciliarVinculo = () =>
  useInvalidatingMutation(({ id, value, justification }: { id: string; value: string; justification: string }) =>
    conciliarVinculo(id, value, justification));

export const useConfirmarVinculo = () => useInvalidatingMutation((id: string) => confirmarVinculo(id));

export const useAtivarVinculo = () => useInvalidatingMutation((id: string) => ativarVinculo(id));

export const useCancelarVinculo = () =>
  useInvalidatingMutation(({ id, justification }: { id: string; justification: string }) => cancelarVinculo(id, justification));

export const useConverterCandidatura = (applicationId: string) =>
  useInvalidatingMutation((payload: ConvertApplicationPayload) => converterCandidatura(applicationId, payload));
